import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MessageSquare, Users, GraduationCap, UserCheck, Send, ArrowLeft, Bell, Mail, Smartphone, Clock, CheckCircle, History, Eye,  Search, ChevronDown, Users as UsersIcon, Loader2, FileText, Upload, Download } from "lucide-react";
// Blue Loader component
const BlueLoader = () => (
  <div className="flex justify-center items-center py-12">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
  </div>
);
import { SidebarProvider } from "@/components/ui/sidebar";
import Adminheader from "./Header";
import Adminsidebar from "./Sidebar";
import { db, auth, storage } from "../firebase/config";
import { collection, query, where, orderBy, getDocs, addDoc, updateDoc, doc, serverTimestamp, onSnapshot, limit, writeBatch, arrayUnion } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import sanitizeHtml from 'sanitize-html';

interface CommunicationCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  recipientCount: number;
  color: string;
  recipients: string;
  disabled?: boolean;
}

interface Conversation {
  id: string;
  title: string;
  allowedEmails: string[];
  participantRoles: string[];
  type: '1:1' | 'group';
  lastMessage: {
    content: string;
    senderEmail: string;
    timestamp: string;
  } | null;
  lastUpdatedAt: string;
  recipients: string;
  recipientCount: number;
  hasReceivedMessages: boolean;
  schoolID: string;
  unreadCount: number;
}

interface Message {
  id: string;
  content: string;
  senderEmail: string;
  timestamp: string;
  readBy: string[];
  priority: string;
  deliveryMethod: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  subject?: string;
  studentName?: string;
  className?: string;
  currentClassUid?: string;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface CustomClaims {
  affiliations?: Affiliation[];
  defaultAffiliation?: Affiliation;
  schoolID?: string;
  role?: string;
}

interface StudentData {
  registrationNumber: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  guardians: { name: string; phone: string; email: string; address: string }[];
  status: string;
  studentUid: string;
  className?: string;
  currentClassUid?: string;
}

interface Memo {
  id: string;
  title: string;
  description: string;
  fileUrl: string;
  fileName: string;
  fileType: string;
  targetAudience: 'staff' | 'teacher-admin' | 'student' | 'parent' | 'everyone';
  createdAt: string;
  senderEmail: string;
  recipientCount: number;
  readBy: string[];
}

const CommunicationManagement = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState<string>("overview");
  const [selectedCategory, setSelectedCategory] = useState<CommunicationCategory | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [showRecipientsModal, setShowRecipientsModal] = useState(false);
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const [priority, setPriority] = useState("normal");
  const [deliveryMethod, setDeliveryMethod] = useState("app");
  const [schoolID, setSchoolID] = useState<string>("");
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [teachers, setTeachers] = useState<User[]>([]);
  const [guardians, setGuardians] = useState<User[]>([]);
  const [students, setStudents] = useState<User[]>([]);
  const [selectedRecipient, setSelectedRecipient] = useState("");
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [classes, setClasses] = useState<{ name: string; classUid: string }[]>([]);
  const [selectedClassUid, setSelectedClassUid] = useState<string>("");
  const [guardianEmailsByClass, setGuardianEmailsByClass] = useState<Record<string, string[]>>({});
  const [recipientSearch, setRecipientSearch] = useState("");
  const [recipientType, setRecipientType] = useState<"teacher" | "guardian" | "student" | null>(null);
  const [returnView, setReturnView] = useState<"received-messages" | "sent-messages">("received-messages");
  const [unreadCount, setUnreadCount] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  
  // Memo States
  const [activeTab, setActiveTab] = useState<"messages" | "memos">("messages");
  const [selectedMemo, setSelectedMemo] = useState<Memo | null>(null);
  const [activeMemoCategory, setActiveMemoCategory] = useState<string | null>(null);
  const [memos, setMemos] = useState<Memo[]>([]);
  const [memoFile, setMemoFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [memoCategories, setMemoCategories] = useState<CommunicationCategory[]>([
    { id: "staff", title: "Staff Memos", description: "Internal Staff Communication", icon: GraduationCap, recipientCount: 0, color: "bg-green-500", recipients: "Teaching & non-teaching staff" },
    { id: "teacher-admin", title: "Teacher–Admin Memos", description: "Between administration and teachers", icon: GraduationCap, recipientCount: 0, color: "bg-blue-500", recipients: "Teachers and Administration" },
    { id: "students", title: "Student Memos", description: "Targeted at students only", icon: Users, recipientCount: 0, color: "bg-orange-500", recipients: "All students" },
    { id: "guardians", title: "Parent Memos", description: "Communication between school and parents", icon: UserCheck, recipientCount: 0, color: "bg-purple-500", recipients: "Parents and Guardians" },
    { id: "everyone", title: "Whole-School Memos", description: "General Notices for everyone", icon: Users, recipientCount: 0, color: "bg-gray-500", recipients: "Staff, Students, and Parents" },
  ]);

  const [communicationCategories, setCommunicationCategories] = useState<CommunicationCategory[]>([
    { id: "everyone", title: "Communicate to Everyone", description: "Send announcements to all school members", icon: Users, recipientCount: 0, color: "bg-blue-500", recipients: "All students, staff, and parents" },
    { id: "staff", title: "Communicate with School Staff", description: "Internal messages for teachers and staff", icon: GraduationCap, recipientCount: 0, color: "bg-green-500", recipients: "Teachers, administrators, and support staff" },
    { id: "guardians", title: "Communicate with Guardians", description: "Updates and notices for parents/guardians", icon: UserCheck, recipientCount: 0, color: "bg-purple-500", recipients: "Parents and legal guardians" },
    { id: "students", title: "Communicate with Students", description: "Direct messages to student body", icon: Users, recipientCount: 0, color: "bg-orange-500", recipients: "All enrolled students" },
  ]);

  const getMessagePreview = (content: string, maxLength: number) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const idTokenResult = await getIdTokenResult(user, true);
          const claims = idTokenResult.claims as CustomClaims;
          const affiliations = claims.affiliations || [];
          const defaultAff = claims.defaultAffiliation;
          const claimSchoolID = claims.schoolID;

          console.log('🔍 Auth claims:', { affiliations, defaultAff, claimSchoolID }); // NEW: Log claims for debug

          const adminAff = defaultAff?.role === "admin" && defaultAff.schoolID
            ? defaultAff
            : affiliations.find(aff => aff.role === "admin" && aff.schoolID);

          if (adminAff?.schoolID && adminAff.schoolID === claimSchoolID) {
            setSchoolID(adminAff.schoolID);
            setHasValidClaims(true);
            setIsAdmin(true);
          } else {
            throw new Error("Invalid admin claims");
          }
        } catch (error: any) {
          console.error("Error verifying user claims:", error);
          // Check if it's a network error
          const isNetworkError = error?.code === 'auth/network-request-failed' ||
                                error?.message?.includes('ERR_NAME_NOT_RESOLVED') ||
                                error?.message?.includes('network-request-failed') ||
                                error?.message?.includes('network');
          
          if (isNetworkError) {
            // Don't logout on network errors, just show a warning
            toast({ 
              title: "Network Error", 
              description: "Unable to verify permissions due to network issues. Please check your connection.", 
              variant: "destructive", 
              duration: 5000 
            });
          } else {
            // Only logout for non-network errors
            toast({ title: "Missing Permissions", description: "You lack permissions to manage communications.", variant: "destructive", duration: 5000 });
            navigate("/login-page");
          }
        }
      } else {
        toast({ title: "Authentication Error", description: "Please log in to continue.", variant: "destructive", duration: 5000 });
        navigate("/login-page");
      }
      setIsAuthLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, toast]);

  const fetchUsers = useCallback(async () => {
    if (!schoolID) return;

    try {
      console.log('🔍 Starting fetchUsers for schoolID:', schoolID);

      // Fetch teachers
      const teachersRef = collection(db, `schools/${schoolID}/teachers`);
      const teacherSnapshot = await getDocs(teachersRef);
      const teacherList: User[] = teacherSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: `${data.firstName} ${data.lastName}`,
          email: sanitizeHtml(data.email || '', { allowedTags: [] }),
          role: 'school_staff',
          subject: data.subjectsOfExpertise?.join(', ') || 'N/A',
        };
      });
      console.log('📚 Teachers snapshot.size:', teacherSnapshot.size, 'teacherList.length:', teacherList.length, teacherList.map(t => ({ name: t.name, email: t.email })));

      // Fetch all students directly from Firestore
      const studentsRef = collection(db, `schools/${schoolID}/students`);
      const allStudentSnapshot = await getDocs(studentsRef);
      const studentList: User[] = [];
      
      for (const docSnap of allStudentSnapshot.docs) {
        const data = docSnap.data() as StudentData;
        let studentEmail = data.email || '';
        
        // If email is missing from document, try to get it from auth user by studentUid
        if (!studentEmail && data.studentUid) {
          console.warn('⚠️ Student missing email in document:', data.studentUid, data.firstName, data.lastName);
          console.log('  Checking if studentUid can be used as auth email lookup...');
          // Note: In a proper setup, you'd store the auth UID or email when creating the student
          // For now, we'll skip students without emails
        }
        
        if (studentEmail) {
          studentList.push({
            id: data.studentUid || docSnap.id,
            name: `${data.firstName} ${data.middleName ? data.middleName + ' ' : ''}${data.lastName}`,
            email: sanitizeHtml(studentEmail, { allowedTags: [] }),
            role: 'student',
            className: data.className,
            currentClassUid: data.currentClassUid,
          });
        }
      }
      
      console.log('👥 Students loaded:', allStudentSnapshot.size, 'with valid emails:', studentList.length, studentList.map(s => ({ name: s.name, email: s.email })));

      // Fetch guardians from student documents AND invitations
      console.log('🛡️ Starting guardian fetch...');
      const guardianEmails = new Set<string>();
      const guardianList: User[] = [];
      const invitationsRef = collection(db, 'invitations');
      const guardianInvites = await getDocs(query(invitationsRef, where('role', '==', 'guardian'), where('schoolID', '==', schoolID), where('status', '==', 'accepted')));
      console.log('📩 Guardian invites (accepted) snapshot.size:', guardianInvites.size, guardianInvites.docs.map(d => ({ email: d.data().email, invitedUid: d.data().invitedUid, guardianUid: d.data().guardianUid })));
      const guardianAuthIds = new Map<string, string>();
      guardianInvites.forEach(doc => {
        const data = doc.data();
        if (data.invitedUid || data.guardianUid) {
          guardianAuthIds.set(data.email, data.invitedUid || data.guardianUid);
        }
      });
      console.log('🔑 Guardian auth IDs mapped:', guardianAuthIds.size, Array.from(guardianAuthIds.entries()));

      // NEW: Add guardians from INVITES (missing before!)
      let guardiansFromInvites = 0;
      for (const docSnap of guardianInvites.docs) {
        const data = docSnap.data();
        if (data.email && !guardianEmails.has(data.email)) {
          // Use guardianName from invitation if present, else fallback to email
          const guardianName = data.guardianName || data.email;
          guardianEmails.add(data.email);
          guardianList.push({
            id: guardianAuthIds.get(data.email) || data.email,
            name: guardianName,
            email: sanitizeHtml(data.email, { allowedTags: [] }),
            role: 'guardian',
            // studentName: For invites, optional—could add logic to link via student if needed
          });
          guardiansFromInvites++;
        }
      }
      console.log('👨‍👩‍👧 Guardians from invites:', guardiansFromInvites);

      // Existing: Add from students (deduped) and build guardianEmailsByClass
      const classGuardiansMap: Record<string, Set<string>> = {};
      let guardianFromStudents = 0;
      allStudentSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data() as StudentData;
        const cUid = data.currentClassUid;
        if (cUid) {
          if (!classGuardiansMap[cUid]) classGuardiansMap[cUid] = new Set();
        }
        data.guardians?.forEach(guardian => {
          if (guardian.email && !guardianEmails.has(guardian.email)) {
            guardianEmails.add(guardian.email);
            guardianList.push({
              id: guardianAuthIds.get(guardian.email) || guardian.email,
              name: guardian.name,
              email: sanitizeHtml(guardian.email, { allowedTags: [] }),
              role: 'guardian',
              studentName: `${data.firstName} ${data.lastName}`,
            });
            guardianFromStudents++;
          }
          if (guardian.email && cUid) {
            classGuardiansMap[cUid].add(guardian.email);
          }
        });
      });
      console.log('👨‍👩‍👧 Guardians from students:', guardianFromStudents);
      const guardianByClass: Record<string, string[]> = {};
      Object.keys(classGuardiansMap).forEach(k => guardianByClass[k] = Array.from(classGuardiansMap[k]));
      setGuardianEmailsByClass(guardianByClass);

      console.log('🛡️ Total guardians (after dedupe):', guardianList.length, guardianList.map(g => ({ name: g.name, email: g.email, studentName: g.studentName })));

      setTeachers(teacherList);
      setStudents(studentList);
      setGuardians(guardianList);

      const totalEveryone = teacherList.length + studentList.length + guardianList.length;
      console.log('📊 Final counts before categories update:', { teachers: teacherList.length, students: studentList.length, guardians: guardianList.length, everyone: totalEveryone });

      setCommunicationCategories(prev => {
        const updated = prev.map(category => ({
          ...category,
          recipientCount: category.id === "everyone" ? totalEveryone :
                          category.id === "staff" ? teacherList.length :
                          category.id === "guardians" ? guardianList.length :
                          category.id === "students" ? studentList.length : 0,
          disabled: category.id === "everyone" ? totalEveryone === 0 :
                    category.id === "staff" ? teacherList.length === 0 :
                    category.id === "guardians" ? guardianList.length === 0 :
                    category.id === "students" ? studentList.length === 0 : true,
        }));
        console.log('📈 Updated categories:', updated.map(c => ({ id: c.id, recipientCount: c.recipientCount, disabled: c.disabled })));
        return updated;
      });

      setMemoCategories(prev => {
        const updated = prev.map(category => ({
          ...category,
          recipientCount: category.id === "everyone" ? totalEveryone :
                          category.id === "staff" ? teacherList.length : // Assuming staff includes teachers for now, ideally should include all staff
                          category.id === "teacher-admin" ? teacherList.length :
                          category.id === "guardians" ? guardianList.length :
                          category.id === "students" ? studentList.length : 0,
          disabled: category.id === "everyone" ? totalEveryone === 0 :
                    category.id === "staff" ? teacherList.length === 0 :
                    category.id === "teacher-admin" ? teacherList.length === 0 :
                    category.id === "guardians" ? guardianList.length === 0 :
                    category.id === "students" ? studentList.length === 0 : true,
        }));
        return updated;
      });
    } catch (error) {
      console.error("Error fetching users:", error);
      console.error('Full error:', error);
      toast({ title: "Error", description: "Failed to load users.", variant: "destructive", duration: 5000 });
    }
  }, [schoolID, toast]);

  const fetchConversations = useCallback(() => {
    if (!schoolID || !auth.currentUser?.email) {
      console.log("Skipping fetchConversations: missing schoolID or user email", { schoolID, email: auth.currentUser?.email });
      return () => {};
    }

    const convCollection = `schools/${schoolID}/conversations`;
    const q = isAdmin
      ? query(collection(db, convCollection), where("schoolID", "==", schoolID), orderBy("lastUpdatedAt", "desc"), limit(20))
      : query(collection(db, convCollection), where("allowedEmails", "array-contains", auth.currentUser.email), where("schoolID", "==", schoolID), orderBy("lastUpdatedAt", "desc"), limit(20));

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const convList: Conversation[] = [];
      let totalUnread = 0;
      for (const doc of snapshot.docs) {
        const data = doc.data();
        const messagesCollection = `schools/${schoolID}/conversations/${doc.id}/messages`;
        try {
          const messageSnapshot = await getDocs(query(collection(db, messagesCollection), orderBy("timestamp", "asc")));
          const hasReceivedMessages = messageSnapshot.docs.some(msg => msg.data().senderEmail !== auth.currentUser?.email);
          const unreadMessages = messageSnapshot.docs.filter(msg => 
            msg.data().senderEmail !== auth.currentUser?.email && 
            !msg.data().readBy?.includes(auth.currentUser?.email || '')
          ).length;
          if (hasReceivedMessages) totalUnread += unreadMessages;

          convList.push({
            id: doc.id,
            title: data.title || '',
            allowedEmails: data.allowedEmails || [],
            participantRoles: data.participantRoles || [],
            type: data.type || 'group',
            lastMessage: data.lastMessage ? {
              content: data.lastMessage.content || '',
              senderEmail: data.lastMessage.senderEmail || '',
              timestamp: data.lastMessage.timestamp?.toDate()?.toLocaleString() || '',
            } : null,
            lastUpdatedAt: data.lastUpdatedAt?.toDate()?.toLocaleString() || '',
            recipients: data.recipients || '',
            recipientCount: data.recipientCount || 0,
            hasReceivedMessages,
            schoolID: data.schoolID || schoolID,
            unreadCount: unreadMessages,
          });
        } catch (error) {
          console.error(`Error fetching messages for conversation ${doc.id}:`, error);
          toast({ title: "Error", description: `Failed to load messages for conversation ${doc.id}.`, variant: "destructive", duration: 5000 });
        }
      }
      setConversations(convList);
      setUnreadCount(totalUnread);
    }, (error) => {
      console.error("Error fetching conversations:", error);
      toast({ title: "Error", description: "Failed to load conversations. Check your permissions.", variant: "destructive", duration: 5000 });
    });

    return unsubscribe;
  }, [schoolID, isAdmin, toast]);

  const fetchMemos = useCallback(() => {
    if (!schoolID) return () => {};
    
    const memosRef = collection(db, `schools/${schoolID}/memos`);
    const q = query(memosRef, orderBy("createdAt", "desc"), limit(50));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const memoList: Memo[] = snapshot.docs.map(doc => ({
        id: doc.id,
        title: doc.data().title,
        description: doc.data().description,
        fileUrl: doc.data().fileUrl,
        fileName: doc.data().fileName,
        fileType: doc.data().fileType,
        targetAudience: doc.data().targetAudience,
        createdAt: doc.data().createdAt?.toDate()?.toLocaleString() || '',
        senderEmail: doc.data().senderEmail,
        recipientCount: doc.data().recipientCount || 0,
        readBy: doc.data().readBy || [],
      }));
      setMemos(memoList);
    });
    return unsubscribe;
  }, [schoolID]);

  const handleViewMemo = async (memo: Memo) => {
    setSelectedMemo(memo);
    // Admins don't usually mark as read since they are the senders, but if they receive memos from others (e.g. super admin), they might.
    // For now, we'll just show it.
  };

  useEffect(() => {
    console.log('useEffect for fetchUsers/conversations trigger:', { schoolID, isAuthLoading, hasValidClaims });
    if (!schoolID || isAuthLoading || !hasValidClaims) return;

    fetchUsers();
    // Load classes for optional class-based recipient filtering
    (async () => {
      try {
        const classesRef = collection(db, `schools/${schoolID}/classes`);
        const snap = await getDocs(classesRef);
        setClasses(snap.docs.map(d => ({ name: d.data().name || d.id, classUid: d.id })));
      } catch (e) {
        console.warn('Failed to load classes:', e);
      }
    })();
    const unsubscribeConv = fetchConversations();
    const unsubscribeMemos = fetchMemos();
    return () => {
      unsubscribeConv();
      unsubscribeMemos();
    };
  }, [schoolID, isAuthLoading, hasValidClaims, fetchUsers, fetchConversations, fetchMemos]);

  const fetchConversationMessages = async (conversationId: string) => {
    setIsLoadingMessages(true);
    try {
      const messagesCollection = `schools/${schoolID}/conversations/${conversationId}/messages`;
      const q = query(collection(db, messagesCollection), orderBy("timestamp", "desc"), limit(50));
      const snapshot = await getDocs(q);
      const messageList: Message[] = snapshot.docs.map(doc => ({
        id: doc.id,
        content: sanitizeHtml(doc.data().content || '', { allowedTags: [] }),
        senderEmail: sanitizeHtml(doc.data().senderEmail || '', { allowedTags: [] }),
        timestamp: doc.data().timestamp?.toDate()?.toLocaleString() || '',
        readBy: doc.data().readBy || [],
        priority: doc.data().priority || 'normal',
        deliveryMethod: doc.data().deliveryMethod || 'app',
      }));

      if (auth.currentUser?.email) {
        const messagesToUpdate = messageList.filter(msg => !msg.readBy.includes(auth.currentUser?.email || ''));
        for (const msg of messagesToUpdate) {
          try {
            await updateDoc(doc(db, messagesCollection, msg.id), {
              readBy: [...msg.readBy, auth.currentUser?.email],
            });
          } catch (error) {
            console.error(`Error updating read status for message ${msg.id}:`, error);
          }
        }
      }

      // Show all messages in conversation details view
      setMessages(messageList);
      
      // Mark unread messages as read in Firestore
      const currentUserEmail = auth.currentUser?.email;
      if (currentUserEmail) {
        const batch = writeBatch(db);
        let hasUpdates = false;
        
        for (const msg of messageList) {
          if (msg.senderEmail !== currentUserEmail && !msg.readBy.includes(currentUserEmail)) {
            const msgRef = doc(db, `schools/${schoolID}/conversations/${conversationId}/messages`, msg.id);
            batch.update(msgRef, {
              readBy: arrayUnion(currentUserEmail)
            });
            hasUpdates = true;
          }
        }
        
        if (hasUpdates) {
          await batch.commit();
        }
      }
      
      // Update conversation unread count to 0 and recalculate total
      setConversations(prev => {
        const updated = prev.map(conv => 
          conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
        );
        const newTotal = updated.reduce((sum, conv) => sum + conv.unreadCount, 0);
        setUnreadCount(newTotal);
        return updated;
      });
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast({ title: "Error", description: "Failed to load messages.", variant: "destructive", duration: 5000 });
      setMessages([]);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleCategorySelect = (category: CommunicationCategory) => {
    if (category.disabled) {
      toast({ title: "Error", description: `No recipients available for ${category.title}.`, variant: "destructive", duration: 5000 });
      return;
    }
    setSelectedCategory(category);
    setSelectedRecipient("");
    setSelectedRecipients([]);
    setSelectedClassUid("");
    setRecipientSearch("");
    setRecipientType(
      category.id === "staff" ? "teacher" :
      category.id === "guardians" ? "guardian" :
      category.id === "students" ? "student" : null
    );
    setActiveView("compose");
  };

  const handleRecipientSelect = (recipient: User) => {
    if (recipientType === "student") {
      setSelectedRecipients(prev => {
        const exists = prev.includes(recipient.email);
        const next = exists ? prev.filter(e => e !== recipient.email) : [...prev, recipient.email];
        const count = next.length;
        const updatedCategory = {
          ...selectedCategory!,
          title: count === 0 ? selectedCategory!.title : `Send to ${count} student${count > 1 ? 's' : ''}`,
          description: count === 0 ? selectedCategory!.description : `Direct message to ${count} selected student${count > 1 ? 's' : ''}`,
          recipientCount: count === 0 ? selectedCategory!.recipientCount : count,
          recipients: count === 0 ? selectedCategory!.recipients : `${count} selected student${count > 1 ? 's' : ''}`,
        };
        setSelectedCategory(updatedCategory);
        return next;
      });
      setRecipientSearch("");
    } else {
      setSelectedRecipient(recipient.email);
      setRecipientSearch("");
      setSelectedCategory({
        ...selectedCategory!,
        title: `Send to ${recipient.name}`,
        description: `Direct message to ${recipient.name} (${recipient.role === 'school_staff' ? `Teacher - ${recipient.subject}` : recipient.role === 'guardian' ? `Parent of ${recipient.studentName}` : 'Student'})`,
        recipientCount: 1,
        recipients: `${recipient.name} - ${recipient.role === 'school_staff' ? `Teacher - ${recipient.subject}` : recipient.role === 'guardian' ? `Parent of ${recipient.studentName}` : 'Student'}`,
      });
    }
  };

  const handleSendMessage = async () => {
    if (!schoolID || !hasValidClaims || !selectedCategory || !auth.currentUser?.email) {
      toast({ title: "Error", description: "Missing permissions or invalid state.", variant: "destructive", duration: 5000 });
      return;
    }

    if (!subject || !message) {
      toast({ title: "Error", description: "Subject and message are required.", variant: "destructive", duration: 5000 });
      return;
    }

    try {
      const convCollection = `schools/${schoolID}/conversations`;
      let conversationId: string;
      let allowedEmails: string[] = [auth.currentUser.email];
      let participantRoles: string[] = ['admin'];
      let convType: '1:1' | 'group' = 'group';
      let recipientCount = selectedCategory.recipientCount;
      let recipients = selectedCategory.recipients;

      // Class-based targeting takes precedence for guardians/students
      if (selectedClassUid && (recipientType === 'guardian' || recipientType === 'student')) {
        let classAllowed: string[] = [];
        if (recipientType === 'guardian') {
          classAllowed = (guardianEmailsByClass[selectedClassUid] || []).filter(e => !!e);
          participantRoles = ['admin', 'guardian'];
          recipients = `Guardians in ${classes.find(c => c.classUid === selectedClassUid)?.name || 'selected class'}`;
        } else {
          classAllowed = students.filter(s => s.currentClassUid === selectedClassUid).map(s => s.email).filter(e => !!e);
          participantRoles = ['admin', 'student'];
          recipients = `Students in ${classes.find(c => c.classUid === selectedClassUid)?.name || 'selected class'}`;
        }
        allowedEmails = [...new Set([auth.currentUser.email, ...classAllowed])];
        convType = 'group';
        recipientCount = classAllowed.length;

        const convDoc = await addDoc(collection(db, convCollection), {
          title: selectedCategory.title,
          allowedEmails,
          participantRoles,
          type: convType,
          lastMessage: null,
          lastUpdatedAt: serverTimestamp(),
          recipients,
          recipientCount,
          schoolID,
        });
        conversationId = convDoc.id;
      } else if (recipientType === 'student' && selectedRecipients.length > 0) {
        const uniqueSelected = Array.from(new Set(selectedRecipients.filter(e => !!e)));
        allowedEmails.push(...uniqueSelected);
        participantRoles = ['admin', 'student'];
        recipientCount = uniqueSelected.length;
        convType = uniqueSelected.length === 1 ? '1:1' : 'group';
        recipients = uniqueSelected.length === 1
          ? (students.find(s => s.email === uniqueSelected[0])?.name || uniqueSelected[0])
          : `${uniqueSelected.length} selected students`;

        if (convType === '1:1') {
          const sortedEmails = allowedEmails.sort();
          const convQuery = query(
            collection(db, convCollection),
            where("type", "==", "1:1"),
            where("allowedEmails", "==", sortedEmails),
            where("schoolID", "==", schoolID)
          );
          const convSnapshot = await getDocs(convQuery);
          if (!convSnapshot.empty) {
            conversationId = convSnapshot.docs[0].id;
          } else {
            const convDoc = await addDoc(collection(db, convCollection), {
              title: selectedCategory.title,
              allowedEmails: sortedEmails,
              participantRoles,
              type: convType,
              lastMessage: null,
              lastUpdatedAt: serverTimestamp(),
              recipients,
              recipientCount,
              schoolID,
            });
            conversationId = convDoc.id;
          }
        } else {
          allowedEmails = [...new Set(allowedEmails)];
          const convDoc = await addDoc(collection(db, convCollection), {
            title: selectedCategory.title,
            allowedEmails,
            participantRoles,
            type: convType,
            lastMessage: null,
            lastUpdatedAt: serverTimestamp(),
            recipients,
            recipientCount,
            schoolID,
          });
          conversationId = convDoc.id;
        }
      } else if (selectedRecipient && recipientType) {
        const recipient = recipientType === "teacher" ? teachers.find(t => t.email === selectedRecipient) :
                         recipientType === "guardian" ? guardians.find(g => g.email === selectedRecipient) :
                         students.find(s => s.email === selectedRecipient);
        if (!recipient) throw new Error("Selected recipient not found.");
        allowedEmails.push(recipient.email);
        participantRoles.push(recipient.role);
        convType = '1:1';
        recipientCount = 1;
        recipients = `${recipient.name} - ${recipient.role === 'school_staff' ? `Teacher - ${recipient.subject}` : recipient.role === 'guardian' ? `Parent of ${recipient.studentName}` : 'Student'}`;

        const sortedEmails = allowedEmails.sort();
        const convQuery = query(
          collection(db, convCollection),
          where("type", "==", "1:1"),
          where("allowedEmails", "==", sortedEmails),
          where("schoolID", "==", schoolID)
        );
        const convSnapshot = await getDocs(convQuery);
        if (!convSnapshot.empty) {
          conversationId = convSnapshot.docs[0].id;
        } else {
          console.log("Creating new conversation:", {
            title: selectedCategory.title,
            allowedEmails: sortedEmails,
            participantRoles,
            type: convType,
            lastMessage: null,
            lastUpdatedAt: serverTimestamp(),
            recipients,
            recipientCount,
            schoolID,
          });
          const convDoc = await addDoc(collection(db, convCollection), {
            title: selectedCategory.title,
            allowedEmails: sortedEmails,
            participantRoles,
            type: convType,
            lastMessage: null,
            lastUpdatedAt: serverTimestamp(),
            recipients,
            recipientCount,
            schoolID,
          });
          conversationId = convDoc.id;
          console.log("Conversation created with ID:", conversationId);
        }
      } else {
        if (selectedCategory.id === "everyone") {
          participantRoles = ['admin', 'school_staff', 'guardian', 'student'];
          const studentEmails = students.map(s => s.email).filter(e => !!e);
          console.log('📧 Adding student emails to everyone conversation:', studentEmails);
          allowedEmails.push(...teachers.map(t => t.email), ...guardians.map(g => g.email), ...studentEmails);
        } else if (selectedCategory.id === "staff") {
          participantRoles = ['admin', 'school_staff'];
          allowedEmails.push(...teachers.map(t => t.email));
        } else if (selectedCategory.id === "guardians") {
          participantRoles = ['admin', 'guardian'];
          allowedEmails.push(...guardians.map(g => g.email));
        } else if (selectedCategory.id === "students") {
          participantRoles = ['admin', 'student'];
          allowedEmails.push(...students.map(s => s.email).filter(e => !!e));
        }

        allowedEmails = [...new Set(allowedEmails)];
        console.log("Creating group conversation:", {
          title: selectedCategory.title,
          allowedEmails,
          participantRoles,
          type: convType,
          lastMessage: null,
          lastUpdatedAt: serverTimestamp(),
          recipients,
          recipientCount,
          schoolID,
        });
        const convDoc = await addDoc(collection(db, convCollection), {
          title: selectedCategory.title,
          allowedEmails,
          participantRoles,
          type: convType,
          lastMessage: null,
          lastUpdatedAt: serverTimestamp(),
          recipients,
          recipientCount,
          schoolID,
        });
        conversationId = convDoc.id;
        console.log("Conversation created with ID:", conversationId);
      }

      const messagesCollection = `schools/${schoolID}/conversations/${conversationId}/messages`;
      const newMessage = {
        content: sanitizeHtml(`${subject}\n\n${message}`, { allowedTags: [] }),
        senderEmail: auth.currentUser.email,
        timestamp: serverTimestamp(),
        readBy: [auth.currentUser.email],
        priority,
        deliveryMethod,
      };

      console.log("Sending message:", newMessage);
      await addDoc(collection(db, messagesCollection), newMessage);
      console.log("Updating conversation:", conversationId);
      await updateDoc(doc(db, convCollection, conversationId), {
        lastMessage: {
          content: newMessage.content,
          senderEmail: auth.currentUser.email,
          timestamp: serverTimestamp(),
        },
        lastUpdatedAt: serverTimestamp(),
      });

      setMessage("");
      setSubject("");
      setPriority("normal");
      setDeliveryMethod("app");
      setSelectedRecipient("");
      setSelectedRecipients([]);
      setRecipientSearch("");
      setRecipientType(null);
      setActiveView("overview");
      setSelectedCategory(null);
      toast({ title: "Success", description: "Message sent successfully.", duration: 3000 });
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast({
        title: error.code === "permission-denied" ? "Permission Error" : "Error",
        description: error.code === "permission-denied" ? "Insufficient permissions to send message." : error.message || "Failed to send message.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleMemoUpload = async () => {
    if (!schoolID || !hasValidClaims || !selectedCategory || !auth.currentUser?.email || !memoFile) {
      toast({ title: "Error", description: "Missing permissions, file, or invalid state.", variant: "destructive", duration: 5000 });
      return;
    }

    if (!subject) {
      toast({ title: "Error", description: "Subject is required for the memo.", variant: "destructive", duration: 5000 });
      return;
    }

    setIsUploading(true);
    try {
      // Upload file to Storage
      const storageRef = ref(storage, `schools/${schoolID}/memos/${Date.now()}_${memoFile.name}`);
      await uploadBytes(storageRef, memoFile);
      const downloadURL = await getDownloadURL(storageRef);

      // Save metadata to Firestore
      const memoData = {
        title: subject,
        description: message, // Using message state for description
        fileUrl: downloadURL,
        fileName: memoFile.name,
        fileType: memoFile.type,
        targetAudience: selectedCategory.id,
        createdAt: serverTimestamp(),
        senderEmail: auth.currentUser.email,
        recipientCount: selectedCategory.recipientCount,
        recipients: selectedCategory.recipients,
      };

      await addDoc(collection(db, `schools/${schoolID}/memos`), memoData);

      toast({ title: "Success", description: "Memo uploaded and sent successfully.", duration: 3000 });
      
      // Reset state
      setMessage("");
      setSubject("");
      setMemoFile(null);
      setSelectedCategory(null);
      setActiveView("overview");
    } catch (error: any) {
      console.error("Error uploading memo:", error);
      toast({ title: "Error", description: "Failed to upload memo.", variant: "destructive", duration: 5000 });
    } finally {
      setIsUploading(false);
    }
  };

  const filteredRecipients = (() => {
    const term = recipientSearch.toLowerCase();
    if (recipientType === "teacher") {
      return teachers.filter(t => t.name.toLowerCase().includes(term) || t.email.toLowerCase().includes(term) || t.subject?.toLowerCase().includes(term));
    }
    if (recipientType === "guardian") {
      let base = guardians.filter(g => g.name.toLowerCase().includes(term) || g.email.toLowerCase().includes(term) || g.studentName?.toLowerCase().includes(term));
      if (selectedClassUid) {
        const allowed = new Set(guardianEmailsByClass[selectedClassUid] || []);
        base = base.filter(g => allowed.has(g.email));
      }
      return base;
    }
    // students
    let base = students.filter(s => s.name.toLowerCase().includes(term) || s.email.toLowerCase().includes(term));
    if (selectedClassUid) base = base.filter(s => s.currentClassUid === selectedClassUid);
    return base;
  })();

  const viewConversationDetails = (conv: Conversation, fromView: "received-messages" | "sent-messages") => {
    setSelectedConversation(conv);
    setReturnView(fromView);
    setMessages([]);
    setShowHistory(false);
    setIsLoadingMessages(true);
    fetchConversationMessages(conv.id);
    setActiveView("conversation-details");
  };

  const getSenderName = (email: string) => {
    return teachers.find(t => t.email === email)?.name || 
           guardians.find(g => g.email === email)?.name || 
           students.find(s => s.email === email)?.name || 
           email;
  };

  const getDisplayTitle = () => {
    if (selectedConversation?.type === '1:1') {
      const otherEmail = selectedConversation.allowedEmails.find(e => e !== auth.currentUser?.email);
      if (otherEmail) {
        const otherName = getSenderName(otherEmail);
        return otherName;
      }
    }
    return selectedConversation?.title || '';
  };

  const getConversationListTitle = (conv: Conversation) => {
    if (conv.type === '1:1') {
      const otherEmail = conv.allowedEmails.find(e => e !== auth.currentUser?.email);
      if (otherEmail) {
        return getSenderName(otherEmail);
      }
    }
    return conv.title;
  };

  if (isAuthLoading || !schoolID || !hasValidClaims) {
    return (
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
          <div className="fixed top-0 left-0 h-screen w-64 z-50">
            <Adminsidebar onSectionChange={() => {}} />
          </div>
          <div className="flex flex-col flex-1 h-full ml-64">
            <Adminheader />
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
              <BlueLoader />
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900 overflow-x-hidden">
        <div className="hidden md:block md:fixed md:top-0 md:left-0 md:h-screen md:w-64 md:z-50">
          <Adminsidebar onSectionChange={() => {}} />
        </div>
        <div className="flex flex-col flex-1 h-full md:ml-64 overflow-x-hidden">
          <Adminheader />
          <main className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6 bg-[#FFFFFF] dark:bg-[#020817]">
            <div className="mb-6 flex justify-center">
              <div className="bg-gray-100 dark:bg-gray-800 p-1 rounded-lg inline-flex">
                <button
                  onClick={() => { setActiveTab("messages"); setActiveView("overview"); setSelectedCategory(null); }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "messages" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"}`}
                >
                  Messages
                </button>
                <button
                  onClick={() => { setActiveTab("memos"); setActiveView("overview"); setSelectedCategory(null); }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "memos" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"}`}
                >
                  Memos
                </button>
              </div>
            </div>

            {activeTab === "messages" ? (
              <>
            {activeView === "conversation-details" && selectedConversation ? (
              <div className="w-full max-w-6xl mx-auto">
                <div className="mb-6 flex items-center gap-4">
                  <Button variant="outline" size="sm" onClick={() => setActiveView(returnView)} className="flex items-center gap-2">
                    <ArrowLeft className="h-4 w-4" /> Back to {returnView === "received-messages" ? "Inbox" : "Sent"}
                  </Button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                  <div className="lg:col-span-3 space-y-6">
                    {/* Email Subject Header */}
                    <div className="bg-white dark:bg-[#020817] p-1">
                        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">{getDisplayTitle()}</h1>
                        <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                            <Badge variant="secondary" className="rounded-md font-normal">{selectedConversation.type === '1:1' ? 'Direct Message' : 'Group Announcement'}</Badge>
                            <span>&bull;</span>
                            <span>{selectedConversation.recipients}</span>
                        </div>
                    </div>

                    {/* Message Thread */}
                    <div className="space-y-4">
                        {isLoadingMessages ? (
                          <div className="flex justify-center py-12">
                            <BlueLoader />
                          </div>
                        ) : messages.length === 0 ? (
                          <div className="text-center py-12 bg-white dark:bg-[#020817] rounded-lg border border-dashed border-gray-200 dark:border-gray-800">
                            <p className="text-gray-500">No messages in this conversation.</p>
                          </div>
                        ) : (
                          <>
                            {(() => {
                              const unreadMessages = messages.filter(m => !m.readBy.includes(auth.currentUser?.email || ''));
                              let displayedMessages = messages;
                              
                              if (!showHistory) {
                                if (unreadMessages.length > 0) {
                                  displayedMessages = unreadMessages;
                                } else {
                                  displayedMessages = [messages[0]];
                                }
                              }

                              return (
                                <>
                                  {displayedMessages.map((msg) => {
                                    const senderName = getSenderName(msg.senderEmail);
                                    const isMe = msg.senderEmail === auth.currentUser?.email;
                                    
                                    let displaySubject = "No Subject";
                                    let displayBody = msg.content;
                                    
                                    const parts = msg.content.split('\n\n');
                                    if (parts.length >= 2) {
                                      displaySubject = parts[0];
                                      displayBody = parts.slice(1).join('\n\n');
                                    } else {
                                        const partsSingle = msg.content.split('\n');
                                        if (partsSingle.length >= 2) {
                                            displaySubject = partsSingle[0];
                                            displayBody = partsSingle.slice(1).join('\n');
                                        }
                                    }

                                    return (
                                      <Card key={msg.id} className="border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden bg-white dark:bg-[#020817] mb-6">
                                        <div className="p-0">
                                            <div className="bg-gray-50/80 dark:bg-gray-900/50 p-6 border-b border-gray-100 dark:border-gray-800 space-y-3">
                                                <div className="grid grid-cols-[60px_1fr] gap-2 items-baseline">
                                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">To</span>
                                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200">{isMe ? selectedConversation.recipients : 'Me'}</span>
                                                </div>
                                                <div className="grid grid-cols-[60px_1fr] gap-2 items-baseline">
                                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">From</span>
                                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200">{senderName} &lt;{msg.senderEmail}&gt;</span>
                                                </div>
                                                <div className="grid grid-cols-[60px_1fr] gap-2 items-baseline">
                                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Date</span>
                                                    <span className="text-sm text-gray-600 dark:text-gray-400">{msg.timestamp}</span>
                                                </div>
                                                <div className="grid grid-cols-[60px_1fr] gap-2 items-baseline">
                                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Subject</span>
                                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{displaySubject}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="p-6 sm:p-8">
                                                <div className="text-base leading-7 text-gray-700 dark:text-gray-300 font-normal whitespace-pre-wrap break-words font-sans">
                                                    {displayBody}
                                                </div>
                                            </div>
                                            
                                            {isMe && (
                                                <div className="px-6 py-3 bg-gray-50/50 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-800 flex justify-end">
                                                    <div className="text-xs font-medium text-gray-500 flex items-center gap-1.5">
                                                        {msg.readBy.length > 1 ? (
                                                            <>
                                                                <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                                                                <span className="text-green-700 dark:text-green-500">Read by {msg.readBy.length - 1} recipient(s)</span>
                                                            </>
                                                        ) : (
                                                            <span>Sent</span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                      </Card>
                                    );
                                  })}
                                  
                                  {!showHistory && messages.length > displayedMessages.length && (
                                    <div className="flex justify-center pt-4">
                                      <Button 
                                        variant="outline" 
                                        onClick={() => setShowHistory(true)}
                                        className="flex items-center gap-2 text-gray-500"
                                      >
                                        <History className="h-4 w-4" />
                                        Show Previous Messages ({messages.length - displayedMessages.length})
                                      </Button>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </>
                        )}
                    </div>
                  </div>

                  {/* Sidebar Context */}
                  <div className="space-y-6 lg:sticky lg:top-6 lg:self-start">
                    <Card className="border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#020817] shadow-sm">
                      <CardHeader className="pb-3 border-b border-gray-100 dark:border-gray-800">
                        <CardTitle className="text-base font-medium">About this conversation</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-4 space-y-4">
                        <div>
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Participants</div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-700 dark:text-gray-300">Total Recipients</span>
                                <Badge variant="secondary" className="font-normal">{selectedConversation.recipientCount}</Badge>
                            </div>
                        </div>
                        
                        <div>
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Roles Involved</div>
                            <div className="flex flex-wrap gap-1.5">
                              {selectedConversation.participantRoles.map((role, index) => (
                                <Badge key={index} variant="outline" className="text-xs font-normal capitalize bg-gray-50 dark:bg-gray-900">{role.replace('_', ' ')}</Badge>
                              ))}
                            </div>
                        </div>

                        <div className="pt-2">
                          <Dialog open={showRecipientsModal} onOpenChange={setShowRecipientsModal}>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" className="w-full justify-start text-gray-600 dark:text-gray-300">
                                <UsersIcon className="h-4 w-4 mr-2" />
                                View All Recipients
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                              <DialogHeader>
                                <DialogTitle>Recipients List</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-2">
                                {selectedConversation.allowedEmails.map((email, index) => (
                                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded border border-[#E2E8F0] dark:border-[#1E293B]">
                                    <span className="text-sm">{getSenderName(email)}</span>
                                    <Badge variant="outline" className="text-xs border border-gray-300 dark:border-gray-700 bg-white/70 dark:bg-[#181C20]/70 shadow-sm rounded-md px-2 py-0.5">{email}</Badge>
                                  </div>
                                ))}
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            ) : activeView === "received-messages" ? (
              <div className="w-full max-w-6xl mx-auto">
                <div className="mb-6 flex items-center gap-4">
                  <Button variant="outline" size="sm" onClick={() => setActiveView("overview")} className="flex items-center gap-2">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </Button>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Received Messages</h1>
                    <p className="text-gray-600 dark:text-gray-400">Messages sent to you</p>
                  </div>
                </div>
                <Card className="m-0 sm:m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Received Messages</CardTitle>
                    <CardDescription>Messages addressed to you</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {conversations.filter(conv => conv.hasReceivedMessages).length === 0 ? (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-4">No messages received yet.</p>
                    ) : (
                      <div className="space-y-4">
                        {conversations.filter(conv => conv.hasReceivedMessages).map((conv) => {
                        const senderEmail = conv.lastMessage?.senderEmail;
                        const senderName = senderEmail ? getSenderName(senderEmail) : 'Unknown';
                        const fromLabel = conv.type === '1:1' ? `From: ${senderName}` : `From Group: ${conv.title}`;
                        return (
                          <div
                            key={conv.id}
                            className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2C2C3B] cursor-pointer transition-colors"
                            onClick={() => viewConversationDetails(conv, "received-messages")}
                          >
                            <div className="flex items-center justify-between p-4 gap-3">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {conv.recipients.includes("All students, staff, and parents") && <Users className="h-4 w-4 text-blue-500" />}
                                  {conv.recipients.includes("Teachers, administrators, and support staff") && <GraduationCap className="h-4 w-4 text-green-500" />}
                                  {conv.recipients.includes("Parents and legal guardians") && <UserCheck className="h-4 w-4 text-purple-500" />}
                                  {conv.recipients.includes("All enrolled students") && <Users className="h-4 w-4 text-orange-500" />}
                                  {conv.type === "1:1" && <UserCheck className="h-4 w-4 text-blue-600" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium truncate">{getConversationListTitle(conv)}</h4>
                                  <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{fromLabel} • {conv.recipientCount} recipients • {conv.lastUpdatedAt}</p>
                                  {conv.lastMessage && (
                                    <p className="text-xs text-gray-500 truncate">Last: {getMessagePreview(conv.lastMessage.content, 50)}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {conv.unreadCount > 0 && (
                                  <Badge variant="destructive" className="text-xs hidden sm:inline-flex">{conv.unreadCount} New</Badge>
                                )}
                                {conv.unreadCount > 0 && (
                                  <div className="h-2 w-2 rounded-full bg-red-500 sm:hidden"></div>
                                )}
                                <Badge variant="outline" className="text-xs hidden sm:inline-flex border border-gray-300 dark:border-gray-700 bg-white/70 dark:bg-[#181C20]/70 shadow-sm rounded-md px-2 py-0.5">{conv.type}</Badge>
                                <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); viewConversationDetails(conv, "received-messages"); }} className="flex items-center gap-1">
                                  <Eye className="h-3 w-3" /> <span className="hidden sm:inline">View</span>
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : activeView === "sent-messages" ? (
              <div className="w-full max-w-6xl mx-auto">
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Button variant="outline" size="sm" onClick={() => setActiveView("overview")} className="flex items-center gap-2">
                      <ArrowLeft className="h-4 w-4" /> Back
                    </Button>
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Sent Messages</h1>
                      <p className="text-gray-600 dark:text-gray-400">View and manage your sent communication history</p>
                    </div>
                  </div>
                </div>
                <Card className="m-0 sm:m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Sent Messages</CardTitle>
                    <CardDescription>All messages you've sent through the communication center</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {conversations.filter(conv => conv.lastMessage && conv.lastMessage.senderEmail === auth.currentUser?.email).length === 0 ? (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-4">No messages sent yet. Start by composing one!</p>
                    ) : (
                      <div className="space-y-4">
                        {conversations.filter(conv => conv.lastMessage && conv.lastMessage.senderEmail === auth.currentUser?.email).map((conv) => (
                        <div
                          key={conv.id}
                          className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2C2C3B] cursor-pointer transition-colors"
                          onClick={() => viewConversationDetails(conv, "sent-messages")}
                        >
                          <div className="flex items-center justify-between p-4 gap-3">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {conv.recipients.includes("All students, staff, and parents") && <Users className="h-4 w-4 text-blue-500" />}
                                {conv.recipients.includes("Teachers, administrators, and support staff") && <GraduationCap className="h-4 w-4 text-green-500" />}
                                {conv.recipients.includes("Parents and legal guardians") && <UserCheck className="h-4 w-4 text-purple-500" />}
                                {conv.recipients.includes("All enrolled students") && <Users className="h-4 w-4 text-orange-500" />}
                                {conv.type === "1:1" && <UserCheck className="h-4 w-4 text-blue-600" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium truncate">{getConversationListTitle(conv)}</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">To: {conv.recipients} • {conv.recipientCount} recipients • {conv.lastUpdatedAt}</p>
                                {conv.lastMessage && (
                                  <p className="text-xs text-gray-500 truncate">Last: {getMessagePreview(conv.lastMessage.content, 50)}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge variant="outline" className="text-xs hidden sm:inline-flex border border-gray-300 dark:border-gray-700 bg-white/70 dark:bg-[#181C20]/70 shadow-sm rounded-md px-2 py-0.5">{conv.type}</Badge>
                              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); viewConversationDetails(conv, "sent-messages"); }} className="flex items-center gap-1">
                                <Eye className="h-3 w-3" /> <span className="hidden sm:inline">View</span>
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : activeView === "compose" && selectedCategory ? (
              <div className="w-full max-w-4xl mx-auto">
                <div className="mb-6 flex items-center gap-4">
                  <Button variant="outline" size="sm" onClick={() => setActiveView("overview")} className="flex items-center gap-2">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </Button>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedCategory.title}</h1>
                    <p className="text-gray-600 dark:text-gray-400">{selectedCategory.recipients}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <Card className="m-0 sm:m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">{React.createElement(selectedCategory.icon, { className: "h-5 w-5" })} Compose Message</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Subject</label>
                          <Input value={subject} onChange={(e) => setSubject(sanitizeHtml(e.target.value, { allowedTags: [] }))} placeholder="Enter message subject..." className="w-full bg-[#FFFFFF] dark:bg-[#020817]" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Priority</label>
                            <Select value={priority} onValueChange={setPriority}>
                              <SelectTrigger className="bg-[#FFFFFF] dark:bg-[#020817]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="low">Low Priority</SelectItem>
                                <SelectItem value="normal">Normal</SelectItem>
                                <SelectItem value="high">High Priority</SelectItem>
                                <SelectItem value="urgent">Urgent</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Delivery Method</label>
                            <Select value={deliveryMethod} onValueChange={setDeliveryMethod}>
                              <SelectTrigger className="bg-[#FFFFFF] dark:bg-[#020817]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="app">App Notification</SelectItem>
                                <SelectItem value="email">Email</SelectItem>
                                <SelectItem value="sms">SMS</SelectItem>
                                <SelectItem value="all">All Methods</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Message</label>
                          <Textarea value={message} onChange={(e) => setMessage(sanitizeHtml(e.target.value, { allowedTags: [] }))} placeholder="Type your message here..." className="min-h-[120px] bg-[#FFFFFF] dark:bg-[#020817]" />
                        </div>
                        <div className="flex justify-end gap-3 mt-4">
                          <Button variant="outline" onClick={() => setActiveView("overview")}>Cancel</Button>
                          <Button onClick={handleSendMessage} disabled={!subject || !message} className="flex items-center gap-2">
                            <Send className="h-4 w-4" /> Send Message
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="space-y-6">
                    <Card className="m-0 sm:m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                      <CardHeader><CardTitle>Recipients</CardTitle></CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Total Recipients</span>
                          <Badge variant="secondary">
                            {(() => {
                              if (recipientType === 'guardian' && selectedClassUid) {
                                return (guardianEmailsByClass[selectedClassUid]?.length || 0).toLocaleString();
                              }
                              if (recipientType === 'student' && selectedClassUid) {
                                return (students.filter(s => s.currentClassUid === selectedClassUid).length).toLocaleString();
                              }
                              if (recipientType === 'student' && selectedRecipients.length > 0) {
                                return selectedRecipients.length.toLocaleString();
                              }
                              if (selectedRecipient) return '1';
                              return selectedCategory.recipientCount.toLocaleString();
                            })()}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${selectedCategory.color}`} />
                          <span className="text-sm">{selectedCategory.recipients}</span>
                        </div>
                        {(selectedCategory.id === 'guardians' || selectedCategory.id === 'students') && (
                          <div className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg bg-gray-50 dark:bg-gray-800 p-2">
                            <label className="block text-sm font-medium mb-2">Filter by Class (Optional)</label>
                            <div className="flex items-center gap-2">
                              <Select value={selectedClassUid} onValueChange={(val) => { setSelectedClassUid(val); setSelectedRecipient(''); setSelectedRecipients([]); }}>
                                <SelectTrigger className="bg-[#FFFFFF] dark:bg-[#020817]"><SelectValue placeholder="All classes" /></SelectTrigger>
                                <SelectContent>
                                  {classes.map((c) => (
                                    <SelectItem key={c.classUid} value={c.classUid}>{c.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {selectedClassUid && (
                                <Button variant="outline" size="sm" onClick={() => setSelectedClassUid("")}>Clear</Button>
                              )}
                            </div>
                            {selectedClassUid && (
                              <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                                Active class filter: {classes.find(c => c.classUid === selectedClassUid)?.name || selectedClassUid}
                              </div>
                            )}
                          </div>
                        )}
                        {(selectedCategory.id === "staff" || selectedCategory.id === "guardians" || selectedCategory.id === "students") && (
                          <div className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg bg-gray-50 dark:bg-gray-800">
                            <label className="block text-sm font-medium mb-2">Select Specific {recipientType === "teacher" ? "Teacher" : recipientType === "guardian" ? "Guardian" : "Student"} (Optional)</label>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="w-full justify-between text-xs">
                                  {recipientType === 'student'
                                    ? (selectedRecipients.length > 0
                                        ? `${selectedRecipients.length} selected`
                                        : `Select a student`)
                                    : (selectedRecipient || `Select a ${recipientType === "teacher" ? "teacher" : recipientType === "guardian" ? "guardian" : "student"}`)
                                  }
                                  <ChevronDown className="h-3 w-3 opacity-50" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="w-full min-w-[300px] bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] shadow-lg z-50">
                                <div className="p-2">
                                  <div className="relative">
                                    <Search className="absolute left-2 top-2.5 h-3 w-3 text-gray-400" />
                                    <Input
                                      placeholder={`Search ${recipientType === "teacher" ? "teachers" : recipientType === "guardian" ? "guardians" : "students"}...`}
                                      value={recipientSearch}
                                      onChange={(e) => setRecipientSearch(sanitizeHtml(e.target.value, { allowedTags: [] }))}
                                      className="pl-7 text-xs h-8 bg-[#FFFFFF] dark:bg-[#020817]"
                                    />
                                  </div>
                                </div>
                                <DropdownMenuSeparator />
                                <div className="max-h-40 overflow-y-auto">
                                  {filteredRecipients.length > 0 ? (
                                    filteredRecipients.map((recipient) => (
                                      <DropdownMenuItem
                                        key={recipient.email}
                                        onClick={() => handleRecipientSelect(recipient)}
                                        className="flex flex-col items-start p-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                                      >
                                        <div className="font-medium text-xs">{recipient.name}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                          {recipientType === "teacher" ? `${recipient.subject} • ${recipient.email}` : 
                                           recipientType === "guardian" ? `Parent of ${recipient.studentName} • ${recipient.email}` : 
                                           `${recipient.email}`}
                                        </div>
                                      </DropdownMenuItem>
                                    ))
                                  ) : (
                                    <DropdownMenuItem disabled className="text-center py-3 text-gray-500 text-xs">
                                      No {recipientType === "teacher" ? "teachers" : recipientType === "guardian" ? "guardians" : "students"} found
                                    </DropdownMenuItem>
                                  )}
                                </div>
                                {(selectedRecipient || (recipientType === 'student' && selectedRecipients.length > 0)) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setSelectedRecipient("");
                                        setSelectedRecipients([]);
                                        setRecipientSearch("");
                                        const originalCategory = communicationCategories.find(cat => cat.id === selectedCategory.id)!;
                                        setSelectedCategory({
                                          ...selectedCategory,
                                          title: originalCategory.title,
                                          description: originalCategory.description,
                                          recipients: originalCategory.recipients,
                                          recipientCount: originalCategory.recipientCount,
                                        });
                                      }}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950 text-xs"
                                    >
                                      Clear selection
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            {recipientType === 'student' && selectedRecipients.length > 0 && (
                              <div className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-blue-50 dark:bg-blue-950/20 rounded p-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Users className="h-3 w-3 text-orange-600" />
                                  <span className="text-xs font-medium text-blue-800 dark:text-blue-200">Selected:</span>
                                  {selectedRecipients.map(email => (
                                    <span key={email} className="flex items-center gap-1 bg-blue-100 dark:bg-blue-900 rounded px-2 py-0.5 text-xs">
                                      {students.find(s => s.email === email)?.name || email}
                                      <button type="button" className="ml-1 text-red-500 hover:text-red-700" onClick={() => setSelectedRecipients(prev => prev.filter(e => e !== email))}>&times;</button>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {recipientType !== 'student' && selectedRecipient && (
                              <div className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-blue-50 dark:bg-blue-950/20 rounded">
                                <div className="flex items-center gap-2">
                                  {recipientType === "teacher" ? (
                                    <GraduationCap className="h-3 w-3 text-green-600" />
                                  ) : recipientType === "guardian" ? (
                                    <UserCheck className="h-3 w-3 text-purple-600" />
                                  ) : (
                                    <Users className="h-3 w-3 text-orange-600" />
                                  )}
                                  <span className="text-xs font-medium text-blue-800 dark:text-blue-200">
                                    Selected: {selectedRecipient}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    <Card className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                      <CardHeader><CardTitle>Delivery Preview</CardTitle></CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center gap-2">
                          {deliveryMethod === "app" && <Bell className="h-4 w-4" />}
                          {deliveryMethod === "email" && <Mail className="h-4 w-4" />}
                          {deliveryMethod === "sms" && <Smartphone className="h-4 w-4" />}
                          {deliveryMethod === "all" && <Users className="h-4 w-4" />}
                          <span className="text-sm capitalize">{deliveryMethod} delivery</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">Estimated delivery: 2-5 minutes</span>
                        </div>
                        {priority === "urgent" && <Badge variant="destructive">Urgent Priority</Badge>}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full max-w-6xl mx-auto">
                <div className="mb-4 sm:mb-6 space-y-4">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">Admin Communication Center</h1>
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Send announcements and manage communications</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button onClick={() => setActiveView("received-messages")} variant="outline" className="flex items-center justify-center gap-2 text-sm">
                      <Mail className="h-4 w-4" /> 
                      <span className="sm:inline">View Received Messages</span>
                      <span className="inline sm:hidden">Received</span>
                      {unreadCount > 0 && (
                        <Badge variant="destructive" className="ml-1">{unreadCount}</Badge>
                      )}
                    </Button>
                    <Button onClick={() => setActiveView("sent-messages")} variant="outline" className="flex items-center justify-center gap-2 text-sm">
                      <History className="h-4 w-4" /> 
                      <span className="sm:inline">View Sent Messages</span>
                      <span className="inline sm:hidden">Sent</span>
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 mb-4 sm:mb-8">
                  {communicationCategories.map((category) => (
                    <Card
                      key={category.id}
                      className={`border border-[#E2E8F0] dark:border-[#1E293B] hover:shadow-lg transition-all cursor-pointer border-l-4 bg-[#FFFFFF] dark:bg-[#020817] ${category.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      style={{ borderLeftColor: category.color.replace('bg-', '') === 'blue-500' ? '#3b82f6' : category.color.replace('bg-', '') === 'green-500' ? '#10b981' : category.color.replace('bg-', '') === 'purple-500' ? '#8b5cf6' : '#f97316' }}
                      onClick={() => handleCategorySelect(category)}
                    >
                      <CardHeader className="p-3 sm:pb-3">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className={`p-1.5 sm:p-2 ${category.color} rounded-lg`}>
                            {React.createElement(category.icon, { className: "h-4 w-4 sm:h-5 sm:w-5 text-white" })}
                          </div>
                          <div>
                            <CardTitle className="text-sm sm:text-lg line-clamp-1">{category.title}</CardTitle>
                            <CardDescription className="text-xs sm:text-sm line-clamp-2">{category.description}</CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-1">{category.recipients}</span>
                          <Badge 
                            variant="outline" 
                            className="text-xs w-fit border border-gray-300 dark:border-gray-700 bg-white/70 dark:bg-[#181C20]/70 shadow-sm rounded-md px-3 py-1"
                          >
                            {
                              selectedCategory?.id === category.id && (
                                selectedRecipient || (recipientType === 'student' && selectedRecipients.length > 0)
                              )
                                ? `${recipientType === 'student' ? selectedRecipients.length : 1} recipient${(recipientType === 'student' ? selectedRecipients.length : 1) > 1 ? 's' : ''}`
                                : `${category.recipientCount.toLocaleString()} recipients`
                            }
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5" /> Recent Messages</CardTitle>
                    <CardDescription>Your latest communication activity</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {conversations.length === 0 ? (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-4">No recent messages. Send your first one today!</p>
                    ) : (
                      conversations.slice(0, 5).map((conv) => (
                        <div key={conv.id} className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2C2C3B]">
                          <div className="flex items-center justify-between p-4">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <h4 className="font-medium truncate">{getConversationListTitle(conv)}</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">To: {conv.recipients} • {conv.lastUpdatedAt}</p>
                                {conv.lastMessage && (
                                  <p className="text-xs text-gray-500 truncate">Last: {getMessagePreview(conv.lastMessage.content, 50)}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge variant="outline" className="border border-gray-300 dark:border-gray-700 bg-white/70 dark:bg-[#181C20]/70 shadow-sm rounded-md px-2 py-0.5">{conv.type}</Badge>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
            </>
            ) : (
              activeView === "compose" && selectedCategory ? (
                <div className="w-full max-w-4xl mx-auto">
                  <div className="mb-6 flex items-center gap-4">
                    <Button variant="outline" size="sm" onClick={() => setActiveView("overview")} className="flex items-center gap-2">
                      <ArrowLeft className="h-4 w-4" /> Back
                    </Button>
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedCategory.title}</h1>
                      <p className="text-gray-600 dark:text-gray-400">{selectedCategory.recipients}</p>
                    </div>
                  </div>
                  <Card className="m-0 sm:m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> Upload Memo</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Subject / Title</label>
                        <Input value={subject} onChange={(e) => setSubject(sanitizeHtml(e.target.value, { allowedTags: [] }))} placeholder="Enter memo title..." className="w-full bg-[#FFFFFF] dark:bg-[#020817]" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Description (Optional)</label>
                        <Textarea value={message} onChange={(e) => setMessage(sanitizeHtml(e.target.value, { allowedTags: [] }))} placeholder="Enter a brief description..." className="min-h-[80px] bg-[#FFFFFF] dark:bg-[#020817]" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Upload File (PDF, Word)</label>
                        <div className="flex items-center gap-4">
                          <Input 
                            type="file" 
                            accept=".pdf,.doc,.docx" 
                            onChange={(e) => setMemoFile(e.target.files ? e.target.files[0] : null)} 
                            className="w-full bg-[#FFFFFF] dark:bg-[#020817]" 
                          />
                        </div>
                        {memoFile && (
                          <div className="mt-4 space-y-2">
                            <p className="text-sm text-green-600 flex items-center gap-2">
                              <CheckCircle className="h-4 w-4" /> Selected: {memoFile.name}
                            </p>
                            {memoFile.type === 'application/pdf' && (
                              <div className="border rounded-lg overflow-hidden h-64 bg-gray-50 dark:bg-gray-900">
                                <iframe 
                                  src={URL.createObjectURL(memoFile)} 
                                  className="w-full h-full" 
                                  title="Memo Preview"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex justify-end gap-3 mt-4">
                        <Button variant="outline" onClick={() => setActiveView("overview")}>Cancel</Button>
                        <Button onClick={handleMemoUpload} disabled={!subject || !memoFile || isUploading} className="flex items-center gap-2">
                          {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} 
                          {isUploading ? "Uploading..." : "Upload & Send Memo"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : selectedMemo ? (
                <div className="w-full max-w-6xl mx-auto">
                  <div className="mb-6 flex items-center gap-4">
                    <Button variant="outline" size="sm" onClick={() => setSelectedMemo(null)} className="flex items-center gap-2">
                      <ArrowLeft className="h-4 w-4" /> Back to List
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-6">
                      <Card className="border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#020817] shadow-sm">
                        <CardHeader className="pb-3 border-b border-gray-100 dark:border-gray-800">
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">{selectedMemo.title}</CardTitle>
                              <CardDescription className="mt-1">
                                Posted on {selectedMemo.createdAt}
                              </CardDescription>
                            </div>
                            <Badge variant="outline" className="capitalize">{selectedMemo.targetAudience.replace('-', ' ')}</Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-6">
                          {selectedMemo.description && (
                            <div>
                              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Description</h3>
                              <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                                {selectedMemo.description}
                              </p>
                            </div>
                          )}
                          
                          <div>
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Document Preview</h3>
                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-900 h-[600px]">
                              {selectedMemo.fileType === 'application/pdf' ? (
                                <iframe 
                                  src={`${selectedMemo.fileUrl}#toolbar=0`}
                                  className="w-full h-full"
                                  title="Memo Document"
                                />
                              ) : (
                                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                                  <FileText className="h-16 w-16 mb-4 opacity-20" />
                                  <p>Preview not available for this file type</p>
                                  <Button variant="outline" className="mt-4" onClick={() => window.open(selectedMemo.fileUrl, '_blank')}>
                                    Download to View
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    <div className="space-y-6">
                      <Card className="border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#020817] shadow-sm">
                        <CardHeader className="pb-3 border-b border-gray-100 dark:border-gray-800">
                          <CardTitle className="text-base font-medium">Memo Details</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4 space-y-4">
                          <div>
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">File Information</div>
                            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <FileText className="h-4 w-4 text-blue-500" />
                              <span className="truncate">{selectedMemo.fileName}</span>
                            </div>
                          </div>
                          
                          <div>
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Target Audience</div>
                            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <Users className="h-4 w-4 text-gray-500" />
                              <span className="capitalize">{selectedMemo.targetAudience.replace('-', ' ')}</span>
                            </div>
                          </div>

                          <div>
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Engagement</div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-700 dark:text-gray-300">Read by</span>
                              <Badge variant="secondary" className="font-normal">
                                {selectedMemo.readBy ? selectedMemo.readBy.length : 0} / {selectedMemo.recipientCount}
                              </Badge>
                            </div>
                            <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-1.5 mt-2">
                              <div 
                                className="bg-green-500 h-1.5 rounded-full transition-all duration-500" 
                                style={{ width: `${Math.min(100, ((selectedMemo.readBy?.length || 0) / (selectedMemo.recipientCount || 1)) * 100)}%` }}
                              />
                            </div>
                          </div>

                          <div className="pt-4">
                            <Button className="w-full" onClick={() => window.open(selectedMemo.fileUrl, '_blank')}>
                              <Download className="h-4 w-4 mr-2" /> Download File
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              ) : activeMemoCategory ? (
                <div className="w-full max-w-6xl mx-auto">
                  <div className="mb-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Button variant="outline" size="sm" onClick={() => setActiveMemoCategory(null)} className="flex items-center gap-2">
                        <ArrowLeft className="h-4 w-4" /> Back to Categories
                      </Button>
                      <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                          {memoCategories.find(c => c.id === activeMemoCategory)?.title}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400">
                          {memoCategories.find(c => c.id === activeMemoCategory)?.description}
                        </p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => {
                        const cat = memoCategories.find(c => c.id === activeMemoCategory);
                        if (cat) handleCategorySelect(cat);
                      }} 
                      className="flex items-center gap-2"
                    >
                      <Upload className="h-4 w-4" /> Upload New Memo
                    </Button>
                  </div>

                  <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                    <CardContent className="p-0">
                      {memos.filter(m => m.targetAudience === activeMemoCategory).length === 0 ? (
                        <div className="text-center py-12">
                          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                            <FileText className="h-8 w-8 text-gray-400" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No memos found</h3>
                          <p className="text-gray-500 dark:text-gray-400">There are no memos in this category yet.</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100 dark:divide-gray-800">
                          {memos.filter(m => m.targetAudience === activeMemoCategory).map((memo) => (
                            <div 
                              key={memo.id} 
                              className="p-4 hover:bg-gray-50 dark:hover:bg-[#2C2C3B] transition-colors cursor-pointer flex items-center justify-between group"
                              onClick={() => handleViewMemo(memo)}
                            >
                              <div className="flex items-center gap-4">
                                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-600 dark:text-blue-400">
                                  <FileText className="h-5 w-5" />
                                </div>
                                <div>
                                  <h4 className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                    {memo.title}
                                  </h4>
                                  <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                    <span>{memo.createdAt}</span>
                                    <span>•</span>
                                    <span>{memo.fileName}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right hidden sm:block">
                                  <div className="text-xs text-gray-500 mb-1">Read by</div>
                                  <Badge variant="secondary" className="font-normal">
                                    {memo.readBy ? memo.readBy.length : 0} / {memo.recipientCount}
                                  </Badge>
                                </div>
                                <ChevronDown className="h-4 w-4 text-gray-400 -rotate-90" />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <div className="w-full max-w-6xl mx-auto">
                  <div className="mb-4 sm:mb-6 space-y-4">
                    <div>
                      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">Memo Management</h1>
                      <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Upload and distribute official memos</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 mb-4 sm:mb-8">
                    {memoCategories.map((category) => (
                      <Card
                        key={category.id}
                        className={`border border-[#E2E8F0] dark:border-[#1E293B] hover:shadow-lg transition-all cursor-pointer border-l-4 bg-[#FFFFFF] dark:bg-[#020817] ${category.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        style={{ borderLeftColor: category.color.replace('bg-', '') === 'blue-500' ? '#3b82f6' : category.color.replace('bg-', '') === 'green-500' ? '#10b981' : category.color.replace('bg-', '') === 'purple-500' ? '#8b5cf6' : '#f97316' }}
                        onClick={() => setActiveMemoCategory(category.id)}
                      >
                        <CardHeader className="p-3 sm:pb-3">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className={`p-1.5 sm:p-2 ${category.color} rounded-lg`}>
                              {React.createElement(category.icon, { className: "h-4 w-4 sm:h-5 sm:w-5 text-white" })}
                            </div>
                            <div>
                              <CardTitle className="text-sm sm:text-lg line-clamp-1">{category.title}</CardTitle>
                              <CardDescription className="text-xs sm:text-sm line-clamp-2">{category.description}</CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="p-3">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-1">{category.recipients}</span>
                            <Badge 
                              variant="outline" 
                              className="text-xs w-fit border border-gray-300 dark:border-gray-700 bg-white/70 dark:bg-[#181C20]/70 shadow-sm rounded-md px-3 py-1"
                            >
                              {memos.filter(m => m.targetAudience === category.id).length} memos
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> Recent Memos</CardTitle>
                      <CardDescription>Recently uploaded memos</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {memos.length === 0 ? (
                        <p className="text-center text-gray-500 dark:text-gray-400 py-4">No memos uploaded yet.</p>
                      ) : (
                        memos.slice(0, 5).map((memo) => (
                          <div 
                            key={memo.id} 
                            className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2C2C3B] cursor-pointer transition-colors"
                            onClick={() => handleViewMemo(memo)}
                          >
                            <div className="flex items-center justify-between p-4">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <h4 className="font-medium truncate">{memo.title}</h4>
                                  <p className="text-sm text-gray-600 dark:text-gray-400 truncate">Target: {memo.targetAudience} • {memo.createdAt}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <Badge variant="secondary" className="text-xs">
                                  {memo.readBy ? memo.readBy.length : 0} reads
                                </Badge>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>
                </div>
              )
            )}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default CommunicationManagement;